import { IPaginatorPageChange } from "dots-ui/dotsPaginator";
import { tableLocalization } from "dots-ui/dotsTable";
import { DotsClassNames } from "dots-ui/components/types";
import { DotsTable, IColumnConfig } from "dots-ui/dotsTable";
import { getLocalization } from "dots-ui/models/Localization";
import { AvisConstants } from "../../../../data/avisConstants";
import { IColumnConfigConditional } from "../../../../models";
import { isMobileView } from "../../appStates/appStates";

interface IAvisSimpleTable<T> {
  columnMetadata: IColumnConfig[];
  data: T[] | undefined;
  mobileCellsVisible?: number;
  columnWidths?: number[];
  rows?: number;
  first?: number;
  totalRecords?: number;
  conditionalFormatting?: IColumnConfigConditional<T>[];
  loading?: boolean;
  mobileViewDisabled?: boolean;
  onPage?: (e: IPaginatorPageChange | undefined) => void;
  rowClassName?: (rowData: T) => string;
}

export function AvisSimpleTable<T extends object>(
  props: Readonly<IAvisSimpleTable<T>>,
) {
  const {
    columnMetadata,
    data,
    mobileCellsVisible = 4,
    columnWidths,
    rows,
    first,
    totalRecords,
    conditionalFormatting,
    loading,
    mobileViewDisabled,
    rowClassName,
    onPage,
  } = props;
  //Minimal totalRecord count to show paginator >10

  const paginatorEnabled =
    totalRecords && data
      ? totalRecords > AvisConstants.grid_paginator_min_row_count
      : false;
  const rowsPerPage = paginatorEnabled
    ? AvisConstants.default_table_page_size_options
    : undefined;

  const customCellClassNames = (
    rowData: T,
    field: Extract<keyof T, string>,
  ): DotsClassNames => {
    if (!conditionalFormatting || conditionalFormatting?.length == 0) return "";

    const foundDefColl = conditionalFormatting.filter(
      (cf) => cf.field === field,
    );

    if (foundDefColl.length === 0) return "";

    const arrayOfFoundClasses: string[] = [];
    foundDefColl.forEach((cDef) => {
      const isConditionTrue = cDef.condition((rowData as any)[field]);
      const classNameResult = isConditionTrue
        ? cDef.trueClass
        : cDef.falseClass;

      if (classNameResult) arrayOfFoundClasses.push(classNameResult);
    });

    return arrayOfFoundClasses.join(" ");
  };

  return (
    <DotsTable<T>
      className="avis-simple-table"
      columnMetaData={columnMetadata}
      columnOrder={columnMetadata.map((item) => item.name)}
      isSortable={false}
      reorderableColumns={true}
      resizableColumns={true}
      totalRecords={totalRecords ?? data?.length}
      value={data}
      gridLines="rows-only"
      rowSpacing="small"
      rowClassName={rowClassName}
      cellClassName={customCellClassNames}
      mobileColumnsVisible={mobileCellsVisible}
      view={
        isMobileView.value && !mobileViewDisabled ? "description-list" : "table"
      }
      pageLinkSize={isMobileView.value && !mobileViewDisabled ? 3 : 5}
      localization={getLocalization(tableLocalization, "lv")}
      columnWidths={columnWidths}
      onPage={onPage}
      paginator={paginatorEnabled}
      rowsPerPageOptions={rowsPerPage}
      rows={rows}
      first={first}
      isLoading={loading}
    />
  );
}
